import { abweisen, ereignis, falten, verlangen, type Ereignis, type GespeichertesEreignis } from "../gemeinsam/index.js";
import { einteilungAnwenden, KEINE_EINTEILUNG, standDesSchuetzen, type EinteilungZustand } from "../drueckjagd/oeffentlich.js";
import { LAGE_EREIGNIS as E } from "./ereignisse.js";
import { geraetepositionLesen, type Anstellstatus, type Geraeteposition } from "./werte.js";

/**
 * Aggregat Anstellung (Domänenmodell §6): eine je Schütze und Drückjagd (`Anstellung:<dj>+<Schütze>`), Bezug auf den
 * zugewiesenen Stand. Statuswechsel nur in Pfeilrichtung: offen → angestellt → am Stand → abgeholt, dazu offen → am Stand.
 *
 * **Standwechsel (P-13).** Wird dem Schützen ein anderer Stand zugewiesen, beginnt die Anstellung für den neuen Stand
 * wieder bei „offen“. Ein eigenes Ereignis dafür gibt es nicht (ereignisspeicher.md §6.4, K24): Der Anstellstatus gilt
 * immer für die aktuelle Zuweisung — er folgt allein aus den Meldungen für den zugewiesenen Stand seit dessen Zuweisung
 * ({@link anstellstatusFuer}). Eine ältere Meldung für einen anderen Stand entfällt damit von selbst.
 */

export interface Anstellmeldung {
  typ: string;
  stand: string;
  serverzeit: Date;
}

export interface AnstellungZustand {
  meldungen: Anstellmeldung[];
}

export const KEINE_ANSTELLUNG: AnstellungZustand = { meldungen: [] };

const STATUS: Record<string, Anstellstatus> = {
  [E.SchuetzeAngestellt]: "angestellt", [E.StandErreicht]: "am Stand", [E.SchuetzeAbgeholt]: "abgeholt",
};

export function anstellungAnwenden(z: AnstellungZustand, e: GespeichertesEreignis): AnstellungZustand {
  if (!STATUS[e.typ]) return z;
  return { meldungen: [...z.meldungen, { typ: e.typ, stand: String(e.daten["Stand"]), serverzeit: e.serverzeit }] };
}

/** Zuweisung eines Schützen: aktueller Stand und Serverzeit, seit der er ihn hält. */
export interface Zuweisung {
  stand: string;
  seit: Date;
}

/**
 * Aktuelle Zuweisung eines Schützen aus den Ereignissen seiner Einteilung (Kontext Drückjagd, `StandSchützenZugewiesen`
 * u. a.); `null` ohne Stand. `seit` ist die Serverzeit des Ereignisses, mit dem er diesen Stand bekam.
 */
export function zuweisungAus(einteilung: readonly GespeichertesEreignis[], schuetze: string): Zuweisung | null {
  let z: EinteilungZustand = KEINE_EINTEILUNG;
  let aktuell = null as Zuweisung | null;
  for (const e of einteilung) {
    z = einteilungAnwenden(z, e);
    const stand = standDesSchuetzen(z, schuetze);
    if (!stand) aktuell = null;
    else if (!aktuell || aktuell.stand !== stand) aktuell = { stand, seit: e.serverzeit };
  }
  return aktuell;
}

/** Anstellstatus für eine Zuweisung: die jüngste Meldung für diesen Stand seit der Zuweisung, sonst „offen“. */
export function anstellstatusFuer(z: AnstellungZustand, zuweisung: Zuweisung | null): Anstellstatus {
  if (!zuweisung) return "offen";
  const gueltig = z.meldungen.filter((m) => m.stand === zuweisung.stand && m.serverzeit.getTime() >= zuweisung.seit.getTime());
  const letzte = gueltig[gueltig.length - 1];
  return letzte ? STATUS[letzte.typ]! : "offen";
}

export function anstellungFalten(ereignisse: readonly GespeichertesEreignis[]): AnstellungZustand {
  return falten(KEINE_ANSTELLUNG, ereignisse, anstellungAnwenden);
}

// ── Befehle ─────────────────────────────────────────────────────────────────────────────────────────────────────

/** Lage des Schützen, wie der Befehl sie aus Einteilung und Anstellung kennt. */
export interface Anstellungslage {
  schuetze: string;
  /** zugewiesener Stand, `null` ohne Zuweisung */
  stand: string | null;
  status: Anstellstatus;
  /** Ansteller der Anstellgruppe des zugewiesenen Standes */
  anstellerDerGruppe: readonly string[];
}

/** Die meldende Person im Revier des Schützen: Teilnehmer und Rollen der Einteilung. */
export interface Anstellungsmelder {
  teilnehmer: string;
  rollen: readonly string[];
}

/** Ansteller der Gruppe oder Jagdleiter (Rollentabelle der Anstellung); sonst `rolle-fehlt` bzw. `nicht-in-eigener-anstellgruppe`. */
function anstellerOderJagdleiter(lage: Anstellungslage, melder: Anstellungsmelder): void {
  if (melder.rollen.includes("Jagdleiter")) return;
  verlangen(melder.rollen.includes("Ansteller"), "rolle-fehlt");
  verlangen(lage.anstellerDerGruppe.includes(melder.teilnehmer), "nicht-in-eigener-anstellgruppe");
}

function statusVerlangen(lage: Anstellungslage, erlaubt: readonly Anstellstatus[]): void {
  verlangen(erlaubt.includes(lage.status), "statuswechsel-unzulaessig");
}

export function schuetzeAngestelltMelden(lage: Anstellungslage, melder: Anstellungsmelder, zeitpunkt: Date): Ereignis[] {
  anstellerOderJagdleiter(lage, melder);
  if (!lage.stand) abweisen("stand-nicht-zugewiesen");
  statusVerlangen(lage, ["offen"]);
  return [ereignis(E.SchuetzeAngestellt, {
    "Schütze": lage.schuetze, Stand: lage.stand, Zeitpunkt: zeitpunkt, "handelnde Person": melder.teilnehmer,
  })];
}

export interface StandErreichtAngaben {
  /** gemeldeter Stand (StandId); ein Schütze meldet nur den ihm zugewiesenen */
  stand: string | undefined;
  position: unknown;
  genauigkeit: unknown;
}

/**
 * „Stand erreicht“: der zugewiesene Schütze selbst, ersatzweise sein Ansteller oder der Jagdleiter; nur für den
 * zugewiesenen Stand (`stand-nicht-zugewiesen`, auch für eine ohne Netz gespeicherte Meldung nach einem Standwechsel).
 */
export function standErreichtMelden(lage: Anstellungslage, melder: Anstellungsmelder, a: StandErreichtAngaben, zeitpunkt: Date): Ereignis[] {
  if (melder.teilnehmer !== lage.schuetze) anstellerOderJagdleiter(lage, melder);
  if (!lage.stand || a.stand !== lage.stand) abweisen("stand-nicht-zugewiesen");
  statusVerlangen(lage, ["offen", "angestellt"]);
  const g: Geraeteposition = geraetepositionLesen(a.position, a.genauigkeit);
  return [ereignis(E.StandErreicht, {
    "Schütze": lage.schuetze, Stand: lage.stand, Position: g.position, Genauigkeit: g.genauigkeit, Zeitpunkt: zeitpunkt,
    "handelnde Person": melder.teilnehmer,
  })];
}

export function schuetzeAbgeholtMelden(lage: Anstellungslage, melder: Anstellungsmelder, zeitpunkt: Date): Ereignis[] {
  anstellerOderJagdleiter(lage, melder);
  if (!lage.stand) abweisen("stand-nicht-zugewiesen");
  statusVerlangen(lage, ["am Stand"]);
  return [ereignis(E.SchuetzeAbgeholt, {
    "Schütze": lage.schuetze, Stand: lage.stand, Zeitpunkt: zeitpunkt, "handelnde Person": melder.teilnehmer,
  })];
}
